/**
 * Pure helpers for the wizard's Step 5 (D1.5.6) module-activation form.
 *
 * Mirrors the canonical 15-module catalog from `packages/core/modules/`
 * and the resolver semantics from `packages/core/tenant/activation.ts`.
 * The browser bundle deliberately doesn't import `@ferro/core` (heavy
 * Prisma/Express transitive deps), so the wizard reproduces the shape
 * statically. The platform-truth resolver runs server-side at commit
 * time (D1.6.1); this module's job is the live preview.
 *
 * If the canonical catalog ever changes, both this file and
 * `MODULE-ACTIVATION-DESIGN-2026-04-21.md §10` must update in lockstep.
 */
export type ModuleSlug =
  | 'tenant-config'
  | 'auth'
  | 'rbac'
  | 'crm'
  | 'messaging'
  | 'booking'
  | 'vaccination'
  | 'payments'
  | 'loyalty'
  | 'staff-scheduling'
  | 'staff-payroll'
  | 'incident-reporting'
  | 'walking'
  | 'ai-manager'
  | 'ai-monitor';

export type ModuleGroup = 'base' | 'data' | 'commerce' | 'staff' | 'operations' | 'ai';

export type ModuleCatalogEntry = {
  slug: ModuleSlug;
  displayName: string;
  description: string;
  group: ModuleGroup;
  isBase: boolean;
  dependencies: ReadonlyArray<ModuleSlug>;
  deactivationWarning: string;
  /**
   * #162 — Whether the platform actually ships a usable backend + admin
   * surface for this module TODAY (distinct from the manifest merely
   * existing in `packages/core/modules/`). A module that is still a
   * manifest-only stub — `ai-monitor`, zero route, zero UI — is
   * `implemented: false`: the wizard renders it as a non-activatable
   * "Coming soon" so an operator can never turn on a feature that 404s.
   * Derived from `COMING_SOON_SLUGS` below.
   */
  implemented: boolean;
};

/**
 * #162 — Catalog modules that are offered in the wizard's catalog SHAPE
 * (to keep lockstep with the canonical 15-slug list) but are NOT yet
 * activatable because they have no backend route and no admin UI. Flip a
 * slug OUT of this set the moment its module ships an actual surface.
 *
 * #773 (2026-08-19) — `staff-payroll` and `ai-manager` came OUT. Both had
 * shipped their surfaces well before this and were still being offered to
 * operators as un-checkable "Coming soon" boxes, which is the inverse of
 * the failure #162 was written to prevent: the wizard was hiding features
 * the platform sells. Evidence, re-verified at removal:
 *   staff-payroll — router mounted at `/api/v2/admin/payroll`
 *     (apps/server/src/index.ts, #427 W3) plus the time-clock admin
 *     surface (`pages/Timeclock.tsx`, `pages/TimeclockManage.tsx`).
 *   ai-manager    — router mounted at `/api/v2/admin/ai` (lane D2/D4)
 *     plus `pages/AiManager.tsx`.
 *   ai-monitor    — STAYS. `packages/core/modules/ai-monitor/` still
 *     contains exactly one file, `module.ts` (the manifest). No route,
 *     no page. This entry is the reason the set still exists.
 *
 * Before removing another slug, check both halves the way the list above
 * does — a mounted route with no page is as much a dead end as neither.
 */
export const COMING_SOON_SLUGS: ReadonlySet<ModuleSlug> = new Set<ModuleSlug>([
  'ai-monitor',
]);

// Authored without `implemented` (it's derived from COMING_SOON_SLUGS just
// below) so the single source of truth for "is this shippable" is one set.
const MODULE_CATALOG_BASE: ReadonlyArray<Omit<ModuleCatalogEntry, 'implemented'>> = [
  // Base (3) — always active.
  {
    slug: 'tenant-config',
    displayName: 'Tenant Configuration',
    description:
      'Tenant-scoped business hours, service catalog, business meta, timezone, currency, branding, and copy terminology.',
    group: 'base',
    isBase: true,
    dependencies: [],
    deactivationWarning:
      'Tenant Config is a base module and cannot be deactivated. Every other module reads from it.',
  },
  {
    slug: 'auth',
    displayName: 'Authentication',
    description:
      'Session management, password and magic-link credentials, and the tenant-scoped authentication middleware.',
    group: 'base',
    isBase: true,
    dependencies: [],
    deactivationWarning:
      'Auth is a base module and cannot be deactivated. Disabling it would prevent every user from signing in.',
  },
  {
    slug: 'rbac',
    displayName: 'Role-Based Access Control',
    description: 'Roles, permission grants, and enforcement middleware used by all other modules.',
    group: 'base',
    isBase: true,
    dependencies: ['auth'],
    deactivationWarning:
      'RBAC is a base module and cannot be deactivated. Every permission check in the platform depends on it.',
  },
  // Data (4)
  {
    slug: 'crm',
    displayName: 'Customer Relationships',
    description:
      'Customers, pets, contact details, and the cross-module activity feed. The substrate every booking, payment, and message threads through.',
    group: 'data',
    isBase: false,
    dependencies: ['rbac'],
    deactivationWarning:
      'Deactivating CRM will hide the customer directory and break every downstream module (bookings, messaging, payments, loyalty). Customer and pet records are preserved and will reappear on reactivation.',
  },
  {
    slug: 'messaging',
    displayName: 'Messaging',
    description:
      'SMS and email conversations with customers, templated outbound messages, and tenant-owned thread history.',
    group: 'data',
    isBase: false,
    dependencies: ['crm'],
    deactivationWarning:
      'Deactivating Messaging pauses outbound SMS/email and hides inbound threads. AI Manager and AI Monitor, which depend on Messaging, will also be blocked. Historical threads are preserved.',
  },
  {
    slug: 'booking',
    displayName: 'Bookings',
    description:
      'Schedule daycare, boarding, grooming, and other services against staff/resource capacity.',
    group: 'data',
    isBase: false,
    dependencies: ['crm', 'rbac'],
    deactivationWarning:
      'Deactivating Bookings will hide the booking calendar, block new reservations, and prevent Payments, Loyalty, and Walking from operating. Existing booking data is preserved and will reappear if Bookings is reactivated.',
  },
  {
    slug: 'vaccination',
    displayName: 'Vaccination Records',
    description:
      'Per-pet vaccination types, due-date tracking, expiration alerts, and vaccination-gated service booking.',
    group: 'data',
    isBase: false,
    dependencies: ['crm'],
    deactivationWarning:
      'Deactivating Vaccination Records hides the vaccination log and stops expiration alerts. Vaccination data is preserved and reappears on reactivation.',
  },
  // Commerce (2)
  {
    slug: 'payments',
    displayName: 'Payments',
    description:
      'Accept card, cash, check, and ACH payments tied to bookings or standalone invoices.',
    group: 'commerce',
    isBase: false,
    dependencies: ['booking', 'crm', 'rbac'],
    deactivationWarning:
      'Deactivating Payments will hide all payment flows, block Loyalty point accrual, and prevent new charges. Historical payment records remain intact.',
  },
  {
    slug: 'loyalty',
    displayName: 'Loyalty & Rewards',
    description:
      'Points accrual on payments, redemption against bookings, and tier-based reward tracking.',
    group: 'commerce',
    isBase: false,
    dependencies: ['crm', 'payments'],
    deactivationWarning:
      'Deactivating Loyalty pauses all points accrual and hides redemption flows. Existing point balances are preserved and will resume earning on reactivation.',
  },
  // Staff (2)
  {
    slug: 'staff-scheduling',
    displayName: 'Staff Scheduling',
    description:
      'Shifts, PTO, coverage requests, and the schedule grid used by managers to staff daily operations.',
    group: 'staff',
    isBase: false,
    dependencies: ['rbac'],
    deactivationWarning:
      'Deactivating Staff Scheduling hides the schedule grid and blocks shift assignments. Payroll and Walking depend on this module and must be deactivated first. Shift history is preserved.',
  },
  {
    slug: 'staff-payroll',
    displayName: 'Staff Payroll',
    description:
      'Timesheets derived from shifts, payroll-export batches, and integration hooks for third-party payroll processors.',
    group: 'staff',
    isBase: false,
    dependencies: ['staff-scheduling', 'payments'],
    deactivationWarning:
      'Deactivating Staff Payroll pauses timesheet collection and hides export flows. Historical payroll runs are preserved.',
  },
  // Operations (2)
  {
    slug: 'incident-reporting',
    displayName: 'Incident Reporting',
    description:
      'Behavior notes, bite reports, photo attachments, and cross-staff handoff documentation.',
    group: 'operations',
    isBase: false,
    dependencies: ['crm', 'rbac'],
    deactivationWarning:
      'Deactivating Incident Reporting hides the incident log and blocks new reports. Existing incidents are preserved and may carry regulatory retention requirements — consult policy before purging.',
  },
  {
    slug: 'walking',
    displayName: 'Dog Walking',
    description: 'Scheduled walks, GPS tracking, walker assignment, photo proofs.',
    group: 'operations',
    isBase: false,
    dependencies: ['booking', 'crm', 'staff-scheduling', 'rbac'],
    deactivationWarning:
      'Deactivating Walking hides walk scheduling, stops GPS tracking, and archives active routes. Historical walk records are preserved.',
  },
  // AI (2)
  {
    slug: 'ai-manager',
    displayName: 'AI Manager',
    description:
      'Conversational AI layer across staff and customer chat — drafts replies, summarizes threads, and enforces tenant voice.',
    group: 'ai',
    isBase: false,
    dependencies: ['messaging', 'crm'],
    deactivationWarning:
      'Deactivating AI Manager stops AI-drafted replies and summarizations. Prior drafts are preserved as read-only records.',
  },
  {
    slug: 'ai-monitor',
    displayName: 'AI Monitor',
    description:
      'Proactive operational alerts: capacity anomalies, projected no-shows, and message-sentiment flags.',
    group: 'ai',
    isBase: false,
    dependencies: ['messaging', 'booking'],
    deactivationWarning:
      'Deactivating AI Monitor stops operational alerts and clears the active alert feed. Alert history is preserved.',
  },
];

// The exported catalog, with `implemented` derived from COMING_SOON_SLUGS so
// the catalog SHAPE stays in lockstep with the canonical 15-slug list while a
// single set governs which modules are actually shippable today.
export const MODULE_CATALOG: ReadonlyArray<ModuleCatalogEntry> = MODULE_CATALOG_BASE.map(
  (m) => ({ ...m, implemented: !COMING_SOON_SLUGS.has(m.slug) }),
);

const CATALOG_BY_SLUG: ReadonlyMap<ModuleSlug, ModuleCatalogEntry> = new Map(
  MODULE_CATALOG.map((m) => [m.slug, m]),
);

export function getModule(slug: string): ModuleCatalogEntry | undefined {
  return CATALOG_BY_SLUG.get(slug as ModuleSlug);
}

export const ALL_MODULE_SLUGS: ReadonlyArray<ModuleSlug> = MODULE_CATALOG.map((m) => m.slug);

export const BASE_MODULE_SLUGS: ReadonlyArray<ModuleSlug> = MODULE_CATALOG.filter(
  (m) => m.isBase,
).map((m) => m.slug);

/** #162 — slugs the platform can actually activate today (not "coming soon"). */
export const IMPLEMENTED_MODULE_SLUGS: ReadonlyArray<ModuleSlug> = MODULE_CATALOG.filter(
  (m) => m.implemented,
).map((m) => m.slug);

/** #162 — true if the module ships a usable backend + UI today (not coming-soon). */
export function isModuleImplemented(slug: string): boolean {
  return getModule(slug)?.implemented ?? false;
}

export const MODULE_GROUPS: ReadonlyArray<ModuleGroup> = [
  'base',
  'data',
  'commerce',
  'staff',
  'operations',
  'ai',
];

export const MODULE_GROUP_LABELS: Record<ModuleGroup, string> = {
  base: 'Base',
  data: 'Data',
  commerce: 'Commerce',
  staff: 'Staff',
  operations: 'Operations',
  ai: 'AI',
};

export const MODULE_GROUP_DESCRIPTIONS: Record<ModuleGroup, string> = {
  base: 'Always active. Cannot be deactivated.',
  data: 'Customer, pet, booking, and messaging substrates.',
  commerce: 'Payments and loyalty/rewards.',
  staff: 'Scheduling, shifts, payroll.',
  operations: 'Incident reporting, walking.',
  ai: 'AI-assisted operational layers.',
};

// ─── Resolver — pure dep closure mirroring packages/core/tenant/activation.ts ─

export type ActivationError =
  | { kind: 'unknown_module'; slug: string }
  | { kind: 'cycle'; path: ReadonlyArray<string> }
  | { kind: 'base_module_immutable'; slug: string }
  | { kind: 'not_currently_active'; slug: string }
  | {
      kind: 'dependant_blocks_removal';
      slug: string;
      activeDependants: ReadonlyArray<ModuleSlug>;
    };

export type ResolveResult =
  | { ok: true; requested: ReadonlyArray<ModuleSlug>; finalSet: ReadonlyArray<ModuleSlug>; autoActivated: ReadonlyArray<ModuleSlug> }
  | { ok: false; error: ActivationError };

/**
 * Resolves the full activation closure for a requested set: base modules +
 * requested + transitive deps, topologically sorted with deps before
 * dependants. Returns `autoActivated = finalSet ∖ requested` so callers
 * can render "also activating: …" chips distinctly from "you asked for …".
 *
 * Mirrors `resolveActivation` in `packages/core/tenant/activation.ts`. The
 * v1 catalog is acyclic by construction, but the cycle check stays in for
 * safety — a future catalog change could introduce one and this would
 * fail loudly rather than infinitely recurse.
 */
export function resolveModuleActivation(requestedSlugs: ReadonlyArray<string>): ResolveResult {
  const seen = new Set<ModuleSlug>();
  const requested: ModuleSlug[] = [];
  for (const raw of requestedSlugs) {
    const entry = getModule(raw);
    if (!entry) return { ok: false, error: { kind: 'unknown_module', slug: raw } };
    if (seen.has(entry.slug)) continue;
    seen.add(entry.slug);
    requested.push(entry.slug);
  }

  // Seed: base modules + requested (deduplicated, base-first).
  const seedSet = new Set<ModuleSlug>();
  const seeds: ModuleSlug[] = [];
  for (const slug of [...BASE_MODULE_SLUGS, ...requested]) {
    if (seedSet.has(slug)) continue;
    seedSet.add(slug);
    seeds.push(slug);
  }

  type Color = 'gray' | 'black';
  const color = new Map<ModuleSlug, Color>();
  const order: ModuleSlug[] = [];

  const visit = (id: ModuleSlug, path: ReadonlyArray<ModuleSlug>): ActivationError | null => {
    const c = color.get(id);
    if (c === 'black') return null;
    if (c === 'gray') return { kind: 'cycle', path: [...path, id] };
    const entry = getModule(id);
    if (!entry) return { kind: 'unknown_module', slug: id };
    color.set(id, 'gray');
    for (const dep of entry.dependencies) {
      const depError = visit(dep, [...path, id]);
      if (depError !== null) return depError;
    }
    color.set(id, 'black');
    order.push(id);
    return null;
  };

  for (const seed of seeds) {
    const seedError = visit(seed, []);
    if (seedError !== null) return { ok: false, error: seedError };
  }

  const requestedSet = new Set(requested);
  const autoActivated = order.filter((s) => !requestedSet.has(s));

  return { ok: true, requested, finalSet: order, autoActivated };
}

/**
 * Validates whether `slug` can be deactivated given the currently-active
 * set. Mirrors `validateDeactivation` in `packages/core/tenant/activation.ts`.
 * Rejects: base modules (immutable), modules not currently active, and
 * modules with at least one currently-active direct dependant.
 */
export function validateDeactivation(
  currentlyActiveSlugs: ReadonlyArray<string>,
  slug: string,
): { ok: true } | { ok: false; error: ActivationError } {
  const entry = getModule(slug);
  if (!entry) return { ok: false, error: { kind: 'unknown_module', slug } };
  if (entry.isBase) return { ok: false, error: { kind: 'base_module_immutable', slug } };
  const active = new Set(currentlyActiveSlugs);
  if (!active.has(slug)) return { ok: false, error: { kind: 'not_currently_active', slug } };
  const dependants: ModuleSlug[] = [];
  const seen = new Set<ModuleSlug>();
  for (const activeSlug of currentlyActiveSlugs) {
    if (activeSlug === slug) continue;
    const m = getModule(activeSlug);
    if (!m) continue;
    if (seen.has(m.slug)) continue;
    if ((m.dependencies as ReadonlyArray<string>).includes(slug)) {
      seen.add(m.slug);
      dependants.push(m.slug);
    }
  }
  if (dependants.length > 0) {
    return {
      ok: false,
      error: { kind: 'dependant_blocks_removal', slug, activeDependants: dependants },
    };
  }
  return { ok: true };
}

// ─── Default-active per vertical ─────────────────────────────────────────────

/**
 * The pre-checked module slugs for a brand-new tenant on a vertical.
 * For pet-services v1: every implemented module the typical SMB needs
 * out of the box (CRM + bookings + payments + messaging) plus the
 * required base set. The admin can deselect anything non-base before
 * commit; if they do, the resolver will warn them what loses access.
 *
 * Stub modules (vaccination, walking, staff-*, ai-*, incident-reporting)
 * default off — they're available for activation but require their
 * implementation to land first. Keeps the wizard honest about what
 * the platform actually delivers on day one.
 */
export function defaultActiveForVertical(vertical: string): ReadonlyArray<ModuleSlug> {
  if (vertical === 'pet-services') {
    return ['crm', 'messaging', 'booking', 'payments'];
  }
  return [];
}

// ─── Draft shape ─────────────────────────────────────────────────────────────

export type ModulesDraft = {
  /**
   * The set of modules the admin has explicitly requested. Excludes
   * base modules (always active) and auto-activated deps (derived).
   * Persisted in the wizard payload under `payload.modules.requested`.
   */
  requested: ReadonlyArray<ModuleSlug>;
};

export function readModulesDraft(payload: Record<string, unknown>): ModulesDraft {
  const raw = payload.modules;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { requested: [] };
  const r = raw as Record<string, unknown>;
  const requested = readSlugList(r.requested);
  return { requested };
}

function readSlugList(value: unknown): ReadonlyArray<ModuleSlug> {
  if (!Array.isArray(value)) return [];
  const seen = new Set<ModuleSlug>();
  const out: ModuleSlug[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const entry = getModule(raw);
    if (!entry) continue;
    if (entry.isBase) continue; // base never appears in `requested`
    if (!entry.implemented) continue; // #162 — coming-soon modules can never be activated, even from a hand-crafted payload
    if (seen.has(entry.slug)) continue;
    seen.add(entry.slug);
    out.push(entry.slug);
  }
  return out;
}

export function setModuleRequested(
  current: ModulesDraft,
  slug: ModuleSlug,
  selected: boolean,
): ModulesDraft {
  const entry = getModule(slug);
  // #162 — base modules are always on; coming-soon modules are never
  // activatable. Either way the request set is unchanged.
  if (!entry || entry.isBase || !entry.implemented) return current;
  const set = new Set(current.requested);
  if (selected) set.add(slug);
  else set.delete(slug);
  // Preserve catalog order so the persisted draft is deterministic.
  const requested = ALL_MODULE_SLUGS.filter((s) => set.has(s) && !getModule(s)!.isBase);
  return { ...current, requested };
}

export function describeActivationError(error: ActivationError): string {
  switch (error.kind) {
    case 'unknown_module':
      return `Unknown module “${error.slug}”.`;
    case 'cycle':
      return `Dependency cycle detected: ${error.path.join(' → ')}.`;
    case 'base_module_immutable':
      return 'Base modules cannot be deactivated.';
    case 'not_currently_active':
      return 'Module is not currently active.';
    case 'dependant_blocks_removal': {
      const names = error.activeDependants
        .map((s) => getModule(s)?.displayName ?? s)
        .join(', ');
      return `Cannot deactivate — these active modules still depend on it: ${names}.`;
    }
  }
}
